// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { OperationType } from './OperationType';
import { OrderType } from './OrderType';
import { OrderStatus } from './OrderStatus';
import { OrderExecutionType } from './OrderExecutionType';
import { TIF } from './OrderTif';
import { OrderTif } from './OrderTifEnum';
import { TifUtils } from './TifUtils';
class _OrderUtils {
    constructor() {
        this.PriceType = { LimitPrice: 1, StopPrice: 2 };
    }
    getBuySellStr(buySell) {
        switch (buySell) {
            case OperationType.Buy:
                return 'Buy'; // Buy Long
            case OperationType.Sell:
                return 'Sell'; // Sell Short
        }
        return 'BuySell: ' + buySell;
    }
    getOrderTypeLocalizationKey(orderType) {
        switch (orderType) {
            case OrderType.Market:
                return 'Market';
            case OrderType.Limit:
                return 'Limit';
            case OrderType.Stop:
                return 'Stop';
            case OrderType.StopLimit:
                return 'Stop Limit';
            case OrderType.TrailingStop:
                return 'Tr. stop';
            case OrderType.OCO:
                return 'OCO';
            case OrderType.SLTPLimit:
                return 'Take-profit';
            case OrderType.SLTPStop:
                return 'Stop-loss';
            case OrderType.TRStop:
                return 'Tr. stop loss';
            case OrderType.Manual:
                return 'Manual';
            case OrderType.Care:
                return 'Care';
        }
        return 'type: ' + orderType;
    }
    GetFormattedLeverage(leverageValue, inShortFormat = false) {
        if (!leverageValue) {
            return '';
        }
        return inShortFormat ? leverageValue.toString() : leverageValue + ' x';
    }
    /// <summary>
    /// #45746 - для Stop и Trailing Stop ореров мы должны отображать цену в колонке StopPrice
    /// </summary>
    /// double limitPrice, double stopPrice, PriceType selectedPriceType, int orderType = 0
    GetCorrectPriceForOrderType(limitPrice, stopPrice, selectedPriceType, orderType = 0) {
        const PriceType = this.PriceType;
        const isStopORTrailingStop = orderType === OrderType.Stop || orderType === OrderType.TrailingStop || orderType === OrderType.SLTPStop;
        if (selectedPriceType === PriceType.LimitPrice && isStopORTrailingStop) {
            return NaN;
        }
        if (selectedPriceType === PriceType.StopPrice && isStopORTrailingStop) {
            return limitPrice;
        }
        if (selectedPriceType === PriceType.LimitPrice) {
            return limitPrice;
        }
        if (selectedPriceType === PriceType.StopPrice) {
            if (orderType === OrderType.Limit || // #94328
                orderType === OrderType.Market ||
                orderType === OrderType.SLTPLimit) {
                return NaN;
            }
            return stopPrice;
        }
        return limitPrice;
    }
    ConvertOrderStatusToExecutionType(orderStatus) {
        switch (orderStatus) {
            case OrderStatus.REPLACED:
                return OrderExecutionType.REPLACED;
            case OrderStatus.PENDING_NEW:
                return OrderExecutionType.PENDING_NEW;
            case OrderStatus.PENDING_EXECUTION:
                return OrderExecutionType.PENDING_EXECUTION;
            case OrderStatus.PENDING_CANCEL:
                return OrderExecutionType.PENDING_CANCEL;
            case OrderStatus.PENDING_REPLACE:
                return OrderExecutionType.PENDING_REPLACE;
            case OrderStatus.NEW:
            case OrderStatus.OFF_MARKET: /* вобщем-то нет соответствия, но мы делаем заглушки: */
            case OrderStatus.UNPLACED:
                return OrderExecutionType.NEW;
            case OrderStatus.ACCEPTED:
                return OrderExecutionType.ACCEPTED;
            case OrderStatus.PART_FILLED:
                return OrderExecutionType.PART_FILLED;
            case OrderStatus.FILLED:
                return OrderExecutionType.FILLED;
            case OrderStatus.CANCELED:
                return OrderExecutionType.CANCELED;
            case OrderStatus.REFUSED:
                return OrderExecutionType.REFUSED;
            case OrderStatus.WAITING_MARKET:
                return OrderExecutionType.ACTIVATED;
            case OrderStatus.WAITING_CANCELLATION:
                return OrderExecutionType.PENDING_CANCEL;
            case OrderStatus.UNKNOWN:
            case OrderStatus.PENDING_REPLACE_NOT_ACTIVE:
            default:
                return OrderExecutionType.UNKNOWN;
        }
    }
    // Метод, который позволяет определить доступный TIF для закрывающих ордеров
    getDefaultSLTPValidity(order, orderType) {
        const tifEnum = OrderTif;
        if (isNullOrUndefined(order)) {
            return new TIF(tifEnum.GTC);
        }
        const allowedTifs = TifUtils.getAllowedTifsForTrading(order.Account, order.Instrument, orderType);
        // #37958 - По умолчанию на СЛ/ТП всегда ставим GTC,
        // если установить GTC невозможно, ставим для sl/tp ТИФ ордера
        const preferredTifs = [tifEnum.GTC, order.TimeInForce, tifEnum.Day, tifEnum.IOC, tifEnum.FOK, tifEnum.GTD];
        for (const tif of preferredTifs) {
            if (allowedTifs.includes(tif)) {
                return tif === tifEnum.GTD ? new TIF(tifEnum.GTD, new Date(order.ExpireAt)) : new TIF(tif);
            }
        }
        return new TIF(tifEnum.GTC);
    }
    isOrderStop(orderType) {
        switch (orderType) {
            case OrderType.Stop:
            case OrderType.StopLimit:
            case OrderType.TrailingStop:
                return true;
            default:
                return false;
        }
    }
    isOrderLimit(orderType) {
        switch (orderType) {
            case OrderType.Limit:
            case OrderType.Care:
                return true;
            default:
                return false;
        }
    }
}
export const OrderUtils = new _OrderUtils();
